import { License, NonAvailableLicense, SelectedContent } from 'apis/TransactionApi';
import { useTransactionDetails } from 'pages/access_codes/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { GMetrixTable } from 'components';
import { Badge } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/access_codes/components/LicenseRestrictionGMetrixTable/styles/license-restriction-gmetrix-table.scss';

interface LicenseRestrictionGMetrixTableProps<T> {
    conflictingDeliverableList: SelectedContent[];
    licenseRestrictions: License[];
    nonAvailableLicenseList: NonAvailableLicense[];
    parentSelectedPendingLicenseRestrictionIndex: number;
    selectedPendingLicenseRestrictions: License[][];
    setSelectedPendingLicenseRestrictions: (licenseRestriction: License[][]) => void;
    valid: boolean;
}

export function LicenseRestrictionGMetrixTable<T>(props: LicenseRestrictionGMetrixTableProps<T>) {

    const { conflictingDeliverableList, licenseRestrictions, nonAvailableLicenseList, parentSelectedPendingLicenseRestrictionIndex,
        selectedPendingLicenseRestrictions, setSelectedPendingLicenseRestrictions, valid } = props;

    const [state, setState] = useSuperState({
        selectedLicenseRestrictions: selectedPendingLicenseRestrictions[parentSelectedPendingLicenseRestrictionIndex] as License[]
    });

    const { selectedLicenseRestrictions } = state;

    const { formatMessage } = useIntl();
    const { pageContext } = useTransactionDetails();

    const onSelectedChange = (e: License[]) => {

        const lastIndexedSelectedPendingLicenseRestriction = e.filter((licenseRestriction, i) => i === e.length - 1);
        const copyParent = [...selectedPendingLicenseRestrictions];
        copyParent[parentSelectedPendingLicenseRestrictionIndex] = lastIndexedSelectedPendingLicenseRestriction;
        setSelectedPendingLicenseRestrictions(copyParent);
        setState({ selectedLicenseRestrictions: lastIndexedSelectedPendingLicenseRestriction });

    };

    return (
        <div className={`${!valid ? 'license-restriction-gmetrix-table__red' : 'license-restriction-gmetrix-table'}`}>
            <div className="license-restriction-gmetrix-table__warning">
                <div className="license-restriction-gmetrix-table__notice-title">
                    <FormattedMessage defaultMessage="Notice" id="notice.e71b4bfa" />
                </div>
                <div className="py-1">
                    {conflictingDeliverableList.length > 1
                        ? <FormattedMessage defaultMessage="The following content are included in multiple licenses." id="the_following_content_are_included_in_mu.9364925e" />
                        : <FormattedMessage defaultMessage="The following piece of content is included in multiple licenses." id="the_following_piece_of_content_is_includ.698d09de" />}
                </div>
                <div className="license-restriction-gmetrix-table__container">
                    {conflictingDeliverableList.map(content => (
                        <Badge
                            pill
                            bg="light"
                            className="border fw-normal"
                            key={`${content.Id}-${content.DeliverableTypeId}`}
                            text="dark"
                        >
                            {content.Name}
                        </Badge>
                    ))}
                </div>
                <div className="pt-1">
                    <FormattedMessage defaultMessage="Please select which license you would like to take the uses from." id="please_select_which_license_you_would_li.20d8bc34" />
                </div>
            </div>
            <GMetrixTable
                columnData={[
                    { field: 'LicenseName', title: formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' }) },
                    { field: 'OrderCreateDateString', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
                    { field: 'OrderStartDateString', title: formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' }) },
                    { field: 'OrderExpireDateString', title: formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' }) },
                    { field: 'PracticeTestUses', title: formatMessage({ id: 'remaining_test_uses.9d587573', defaultMessage: 'Remaining Test Uses' }) },
                    { field: 'CourseUses', title: formatMessage({ id: 'remaining_course_uses.b8c5bd52', defaultMessage: 'Remaining Course Uses' }) },
                    { field: 'EbookUses', title: formatMessage({ id: 'remaining_ebook_uses.7246c293', defaultMessage: 'Remaining EBook Uses' }) }
                ]}
                bottomBarTableName={formatMessage({ id: 'restrictions.16d3abf7', defaultMessage: 'Restrictions' })}
                loading={false}
                multiSelect={false}
                onSelectedChange={onSelectedChange}
                rowData={licenseRestrictions}
                selectableRows={true}
                selected={selectedLicenseRestrictions}
            />
            {nonAvailableLicenseList.length > 0 && pageContext?.Data.transactionDetails?.ExpirationDateString && (
                <>
                    <div className="license-restriction-gmetrix-table__warning">
                        <div className="py-2">
                            <FormattedMessage defaultMessage="Warning" id="warning.64b35335" />
                            :
                        </div>
                        <div className="py-2">
                            {nonAvailableLicenseList.length > 1
                                ? <FormattedMessage defaultMessage={'The following piece of contents are part of a License that has an expiration\r\n      date that occurs sooner than the currently selected Access Code Due Date'} id="the_following_piece_of_contents_are_part.56adc084" />
                                : <FormattedMessage defaultMessage={'The following piece of content is part of a License that has an expiration\r\n      date that occurs sooner than the currently selected Access Code Due Date'} id="the_following_piece_of_content_is_part_o.fd79a8ff" />}
                            {' '}
                            {pageContext.Data.transactionDetails.ExpirationDateString}
                            .
                        </div>
                        <ul>
                            {nonAvailableLicenseList.map((content, index) => (
                                <li className="py-2" key={index}>
                                    <div>
                                        <FormattedMessage defaultMessage="Piece Of Content" id="piece_of_content.212d2b33" />
                                        :
                                        {content.content.Name}
                                    </div>
                                    <div>
                                        <FormattedMessage defaultMessage="Language" id="language.bc205d83" />
                                        :
                                        {content.license.LicenseName}
                                    </div>
                                    <div>
                                        <FormattedMessage defaultMessage="Expiration" id="expiration.c3d7aca4" />
                                        :
                                        {content.license.OrderExpireDateString}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="pb-2">
                            {nonAvailableLicenseList.length > 1
                                ? <FormattedMessage defaultMessage={'Unless the licenses above are renewed, students in this Access Code\r\n      will lose access to the above products when the license expires.'} id="unless_the_licenses_above_are_renewed_st.44e572c9" />
                                : <FormattedMessage defaultMessage={'Unless the license above is renewed, students in this Access Code\r\n      will lose access to the above products when the license expires.'} id="unless_the_license_above_is_renewed_stud.b532c187" />}
                        </div>
                    </div>
                </>
            )}
        </div>
    );

}
